"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight, Sparkles, Gauge, TrendingUp, Star } from "lucide-react";
import Image from "next/image";
import DiversityInsightsBox from "./closerLook/AiRecommendation";
import Pill from "../../home/AICreativeSystemsSection/Pill";
import WhereYouLead from "./closerLook/WhereYouLead";
import AiPlanAction from "./closerLook/AiPlanAction";
import CompetitorTracker from "./closerLook/CompetitorTracker";
import AiRecommendation from "./closerLook/AiRecommendation";

const tabs = [
  {
    id: "creative-analysis",
    label: "Creative Analysis",
    pill: "Analyze",
    heading: ["Score and rank your creative by ", "Performance signal"],
    desc: "Upload or connect your ad creatives to uncover winning patterns, performance drivers, and optimization opportunities. Instantly understand which creatives to scale, improve, or pause based on real campaign data.",
    image: "/assets/creativeIntelligence/1.svg",
    imageW: 668,
    imageH: 407,
    contentRatio: 1,
    rows: [
      {
        Icon: Star,
        title: "AI Summary & Key Insights",
        desc: "Get an AI-generated digest of your account performance, highlighting top-performing creatives.",
      },
      {
        Icon: Star,
        title: "Smart Recommendation Engine",
        desc: "Receive actionable recommendations on which creatives to scale, pause, or refresh to maximize advertising performance.",
      },
      {
        Icon: Star,
        title: "Performance Trend Analysis",
        desc: "Track spend, impressions, conversions, CTR, CPA, and ROAS over time to identify performance shifts and opportunities.",
      },
    ],
    cta: "Try the Creative Analyzer",
    href: "https://app.imagive.ai?redirect=/users/competitive-analysis",
  },

  {
    id: "performance-analysis",
    label: "Performance Analysis",
    pill: "Analyze",
    heading: ["Discover the patterns behind your", " best-performing ads"],
    desc: "Analyze creative attributes across your campaigns to uncover winning signals, identify underperforming patterns, and understand exactly which elements drive higher ROAS, lower CPA, and stronger engagement.",
    image: "/assets/creativeIntelligence/2.svg",
    imageW: 668,
    imageH: 407,
    contentRatio: 1,
    rows: [
      {
        Icon: Star,
        title: "Winning Signals",
        desc: "Uncover the creative elements driving exceptional performance across your ad account.",
      },
      {
        Icon: Star,
        title: "Pattern Explorer",
        desc: "Analyze how specific creative attributes impact ROAS, CPA, CTR, and conversions.",
      },
      {
        Icon: Star,
        title: "Creative Insights",
        desc: "Discover which creatives to scale, improve, test, or pause based on real performance data.",
      },
    ],

    cta: "Run a prediction",
    href: "https://app.imagive.ai?redirect=/users/creative-analytics",
  },
  {
    id: "competitor-tracker",
    label: "Competitor Tracker",
    pill: "Analyze",
    heading: [
      "Outsmart competitors with AI-powered ",
      "competitive intelligence",
    ],
    desc: "Understand how your brand compares against competitors, uncover winning creative patterns, identify market gaps, and receive an actionable plan to improve performance faster.",
    image: "/assets/creativeIntelligence/3.svg",
    imageW: 610,
    imageH: 486,
    // artwork ends at y=371.65 of 486 — bottom 23.5% of the file is empty
    contentRatio: 0.7647,
    rows: [
      {
        Icon: Star,
        title: "Competitive Benchmarking",
        desc: "Compare ad volume, creative formats, messaging strategies, buyer motivations, hooks, and audience positioning against top competitors in your market.",
      },
      {
        Icon: Star,
        title: "AI-Powered Plan of Action",
        desc: "Discover exactly what to improve, what to test next, and where to focus resources for maximum growth.",
      },
      {
        Icon: Star,
        title: "Executive Competitive Insights",
        desc: "Get an AI-generated summary of where you lead, where competitors perform",
      },
    ],
    cta: "Try the Creative Analyzer",
    href: "https://app.imagive.ai?redirect=/users/competitive-analysis",
    box: <CompetitorTracker />,
  },
  {
    id: "ai-recommendations",
    label: "AI Recommendations",
    pill: "AI",
    heading: ["Turn insights into action with AI-powered recommendations", ""],
    desc: "Stop guessing what to optimize next. AI analyzes your creatives, identifies top-performing and underperforming ads, and builds a prioritized action plan to improve campaign performance.",
    image: "/assets/creativeIntelligence/4.svg",
    imageW: 610,
    imageH: 486,
    // same trailing whitespace as 3.svg
    contentRatio: 0.7647,
    rows: [
      {
        Icon: Star,
        title: "AI Plan of Action",
        desc: "Receive prioritized recommendations ranked by expected impact and effort.",
      },
      {
        Icon: Star,
        title: "Underperforming Ad Detection",
        desc: "Identify creatives that are losing efficiency, showing signs of fatigue.",
      },
      {
        Icon: Star,
        title: "Smart Creative Generation",
        desc: "Generate improved creative variations directly from recommendations.",
      },
    ],
    cta: "Try the Creative Analyzer",
    href: "https://app.imagive.ai?redirect=/users/competitive-analysis",
    box: <AiRecommendation />,
  },
];

const CloserLook = () => {
  const [active, setActive] = useState(tabs[0].id);
  const [userInteracted, setUserInteracted] = useState(false);
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || userInteracted) return;
    const timer = setInterval(() => {
      setActive((prev) => {
        const i = tabs.findIndex((t) => t.id === prev);
        return tabs[(i + 1) % tabs.length].id;
      });
    }, 3500);
    return () => clearInterval(timer);
  }, [inView, userInteracted]);

  const current = tabs.find((t) => t.id === active) ?? tabs[0];

  return (
    <section className="bg-white px-5 pt-10 pb-16 sm:px-6 sm:pt-6 lg:px-20 lg:pt-10">
      {/* ── Header ── */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.5fr_1fr] lg:items-start lg:gap-16">
        <h2 className="solution-sub-heading max-w-[600px] 2xl:max-w-[700px] 3xl:max-w-[800px] 4xl:max-w-[900px] ">
          A closer look at every Layer of{" "}
          <span className="text-[#3232AA]">Creative Intelligence.</span>
        </h2>
        <p className="body-secondary-text  max-w-[600px] 2xl:max-w-[700px] 3xl:max-w-[800px] ">
          Tap a tab or scroll through to see exactly what each engine measures,
          predicts and recommends. Real metrics. Real benchmarks. Real outputs.
        </p>
      </div>

      {/* ── Tabbed container ── */}
      <div className="mt-10 overflow-hidden rounded-[24px] border border-black/10 bg-white lg:mt-14">
        {/* Tabs */}
        <div
          ref={ref}
          className="flex w-full overflow-x-auto border-b border-black/10 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {tabs.map((tab, i) => {
            const isActive = active === tab.id;
            const num = String(i + 1).padStart(2, "0");
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setUserInteracted(true);
                  setActive(tab.id);
                }}
                className={`-mb-px flex shrink-0 items-center gap-2 whitespace-nowrap border-b-[2.5px] px-5 mx-3 py-4 text-[13px] transition-colors lg:px-2 lg:py-5 lg:text-sm ${
                  isActive
                    ? "border-[#0F1118] text-[#0F1118]"
                    : "border-transparent text-[#9CA3AF] hover:text-[#0F1118]"
                }`}
              >
                <span
                  className={isActive ? "text-[#9CA3AF]" : "text-[#C7C7CC]"}
                >
                  {num}
                </span>
                <span className={isActive ? "font-medium" : "font-normal"}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Panel */}
        <div
          key={current.id}
          className="tab-fade-in grid grid-cols-1 lg:grid-cols-2 lg:items-center"
        >
          {/* Copy */}
          <div className="order-2 p-6 sm:p-8 lg:order-1 lg:p-12">
            <span className="inline-flex items-center rounded-full bg-[#F1F1F3] px-3 py-1 text-[12px] font-medium text-[#53555C]">
              {current.pill}
            </span>

            <h3 className="mt-5 text-[26px] font-medium leading-[1.12] tracking-[-0.5px] text-[#242424] sm:text-[30px] lg:text-[36px] 3xl:text-[44px]">
              {current.heading[0]}
              <span className="text-[#3232AA]">{current.heading[1]}</span>
            </h3>

            <p className="mt-4 max-w-[480px] text-[14px] leading-[1.6] text-zinc-500 sm:text-[15px] 3xl:text-base">
              {current.desc}
            </p>

            <div className="mt-7 flex flex-col gap-5">
              {current.rows.map(({ title, desc, Icon }) => (
                <div key={title} className="flex items-start gap-3.5">
                  <span className="flex items-center h-9 w-9 shrink-0 rounded-[10px] bg-[#0F1118]">
                    <Icon size={24} className="m-auto h-6 w-6 text-white " />
                  </span>

                  <div>
                    <h4 className="text-[15px] font-semibold tracking-[-0.2px] text-[#0F1118]">
                      {title}
                    </h4>
                    <p className="mt-1 max-w-[400px] text-[13px] leading-[1.55] text-zinc-500 3xl:text-[14px]">
                      {desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <a
              href={current.href}
              target="_blank"
              className="group mt-8 inline-flex items-center gap-1.5 text-[14px] font-medium text-[#3232AA]"
            >
              {current.cta}
              <ArrowRight
                size={15}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </a>
          </div>

          {/* Visual — laptop pinned to the right wall, bleeding behind it */}
          <div className="relative order-1 overflow-hidden px-6 pt-8 lg:order-2 lg:overflow-visible lg:p-0">
            <div className="pointer-events-none absolute right-0 top-1/2 h-[70%] w-[65%] -translate-y-1/2 rounded-full bg-gradient-to-b from-[#C7DEFF] to-[#FF9FD4] blur-[100px]" />

            <div className="relative ml-[8%] aspect-[668/407] w-[105%] lg:ml-[14%] lg:w-[108%]">
              <Image
                key={current.image}
                src={current.image}
                alt={`${current.label} preview`}
                width={current.imageW}
                height={current.imageH}
                style={{ height: `${100 / current.contentRatio}%` }}
                className="absolute left-0 top-0 w-full max-w-none"
              />

              <div
                className={`absolute origin-left -translate-y-1/2 scale-[0.78] lg:origin-center lg:-translate-x-1/2 lg:scale-100 2xl:scale-110 ${
                  current.id === "ai-recommendations"
                    ? "left-[-10%] top-[40%] lg:left-[4%] lg:top-[35%]"
                    : "left-[-2%] top-[48%] lg:left-[14%] lg:top-[52%]"
                }`}
              >
                {current.box && current.box}
              </div>

              {current.id === "competitor-tracker" && (
                <div className="absolute left-[10%] top-[-4%] origin-left -translate-y-1/2 scale-[0.78] lg:left-[-10%] lg:top-[25%] lg:scale-100 2xl:scale-110">
                  <WhereYouLead />
                </div>
              )}

              {current.id === "ai-recommendations" && (
                <div className="absolute left-[10%] top-[8%] origin-left -translate-y-1/2 scale-[0.78] lg:left-[-16%] lg:top-[13%] lg:scale-100 2xl:scale-110">
                  <AiPlanAction />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CloserLook;
